import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetchJson } from '../../apiClient';
import BookCover from './BookCover';
import './MyLibrary.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

const OWNERSHIP_TYPES = ['All', 'Owned', 'Digital', 'Borrowed', 'Wishlist'];

// eslint-disable-next-line no-unused-vars
const OWNERSHIP_COLORS = {
  Owned: 'green',
  Digital: 'blue',
  Borrowed: 'orange',
  Wishlist: 'purple',
};

function renderDifficultyStars(score) {
  if (!score) return null;
  const full = Math.round(score);
  return (
    <span className="my-library-stars" title={`Difficulty: ${score}/5`}>
      {'★'.repeat(full)}{'☆'.repeat(5 - full)}
    </span>
  );
}

function MyLibrary({ factFictionMode, navigateTo }) {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const [books, setBooks] = useState([]);
  const [shelves, setShelves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeShelf, setActiveShelf] = useState('all');
  const [ownershipFilter, setOwnershipFilter] = useState('All');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const fetchLibrary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let url = `/api/userlibrary?type=${factFictionMode}`;
      if (activeShelf !== 'all') {
        url += `&shelf=${encodeURIComponent(activeShelf)}`;
      }
      if (ownershipFilter !== 'All') {
        url += `&ownership=${ownershipFilter}`;
      }

      const [booksData, shelvesData] = await Promise.all([
        apiFetchJson(instance, url),
        apiFetchJson(instance, '/api/userlibrary/shelves'),
      ]);

      setBooks(unwrap(booksData));
      setShelves(unwrap(shelvesData));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [instance, factFictionMode, activeShelf, ownershipFilter]);

  useEffect(() => {
    fetchLibrary();
  }, [fetchLibrary]);

  const handleOwnershipChange = useCallback(async (entryId, newOwnership) => {
    try {
      const entry = books.find(b => b.id === entryId);
      if (!entry) return;

      await apiFetchJson(instance, `/api/userlibrary/${entryId}`, {
        method: 'PUT',
        body: JSON.stringify({ ownership: newOwnership, shelfName: entry.shelfName }),
      });

      setBooks(prev => prev.map(b => b.id === entryId ? { ...b, ownership: newOwnership } : b));
    } catch (err) {
      setError(err.message);
    }
  }, [instance, books]);

  const handleMoveToShelf = useCallback(async (entryId, newShelf) => {
    try {
      const entry = books.find(b => b.id === entryId);
      if (!entry) return;

      await apiFetchJson(instance, `/api/userlibrary/${entryId}`, {
        method: 'PUT',
        body: JSON.stringify({ ownership: entry.ownership, shelfName: newShelf }),
      });

      setBooks(prev => prev.map(b => b.id === entryId ? { ...b, shelfName: newShelf } : b));
    } catch (err) {
      setError(err.message);
    }
  }, [instance, books]);

  const handleRemove = useCallback(async (entryId) => {
    try {
      await apiFetchJson(instance, `/api/userlibrary/${entryId}`, {
        method: 'DELETE',
      });

      setBooks(prev => prev.filter(b => b.id !== entryId));
      setConfirmDeleteId(null);
    } catch (err) {
      setError(err.message);
    }
  }, [instance]);

  const totalBooks = books.length;

  if (loading) {
    return (
      <div className="my-library-loading">
        <div className="my-library-spinner" />
        <p>{t('bookClub.myLibrary.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-library-error">
        <p>⚠️ {error}</p>
        <button className="my-library-retry-btn" onClick={fetchLibrary}>{t('bookClub.common.retry')}</button>
      </div>
    );
  }

  return (
    <div className="my-library">
      {/* Stats header */}
      <div className="my-library-stats">
        <span className="my-library-stat">📚 <strong>{totalBooks}</strong> {t('bookClub.common.books')}</span>
        <span className="my-library-stat">
          {t('bookClub.myLibrary.viewing')} <strong>{factFictionMode === 'fiction' ? '📖 ' + t('bookClub.fiction') : '📚 ' + t('bookClub.fact')}</strong>
        </span>
      </div>

      <div className="my-library-layout">
        {/* Shelf sidebar */}
        <aside className="my-library-shelves">
          <h3 className="my-library-shelves-title">{t('bookClub.myLibrary.shelves')}</h3>
          <button
            className={`my-library-shelf-pill ${activeShelf === 'all' ? 'active' : ''}`}
            onClick={() => setActiveShelf('all')}
          >
            {t('bookClub.myLibrary.allBooks')}
          </button>
          {shelves.map(shelf => (
            <button
              key={shelf.shelfName}
              className={`my-library-shelf-pill ${activeShelf === shelf.shelfName ? 'active' : ''}`}
              onClick={() => setActiveShelf(shelf.shelfName)}
            >
              {shelf.shelfName} <span className="my-library-shelf-count">{shelf.count}</span>
            </button>
          ))}
        </aside>

        <div className="my-library-main">
          {/* Filter bar */}
          <div className="my-library-filters">
            {OWNERSHIP_TYPES.map(type => (
              <button
                key={type}
                className={`my-library-filter-btn ${ownershipFilter === type ? 'active' : ''}`}
                onClick={() => setOwnershipFilter(type)}
              >
                {type}
              </button>
            ))}
          </div>

          {/* Book grid or empty state */}
          {books.length === 0 ? (
            <div className="my-library-empty">
              <span className="my-library-empty-icon">📖</span>
              <p>{t('bookClub.myLibrary.emptyTitle')}</p>
              {navigateTo && <button className="bookclub-nav-link" onClick={() => navigateTo('search')}>🔎 {t('bookClub.common.searchBooks')}</button>}
            </div>
          ) : (
            <div className="my-library-grid">
              {books.map(entry => (
                <div key={entry.id} className="my-library-card">
                  <div className="my-library-card-cover">
                    <BookCover src={entry.book?.coverImageUrl} alt={entry.book?.title} className="my-library-cover" emoji="📕" />
                  </div>

                  <div className="my-library-card-info">
                    <h4 className="my-library-card-title" title={entry.book?.title}>{entry.book?.title}</h4>
                    <p className="my-library-card-author">{entry.book?.author}</p>

                    <span className={`my-library-badge my-library-badge--${(entry.ownership || '').toLowerCase()}`}>
                      {entry.ownership}
                    </span>

                    {entry.book?.difficultyScore != null && renderDifficultyStars(entry.book.difficultyScore)}

                    {entry.book?.pageCount > 0 && (
                      <span className="my-library-page-count">{entry.book.pageCount} {t('bookClub.common.pages')}</span>
                    )}
                  </div>

                  <div className="my-library-card-actions">
                    <select
                      className="my-library-select"
                      value={entry.ownership || ''}
                      onChange={(e) => handleOwnershipChange(entry.id, e.target.value)}
                    >
                      {OWNERSHIP_TYPES.filter(t => t !== 'All').map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>

                    <select
                      className="my-library-select"
                      value={entry.shelfName || ''}
                      onChange={(e) => handleMoveToShelf(entry.id, e.target.value)}
                    >
                      <option value="">{t('bookClub.myLibrary.noShelf')}</option>
                      {shelves.map(s => (
                        <option key={s.shelfName} value={s.shelfName}>{s.shelfName}</option>
                      ))}
                    </select>

                    {confirmDeleteId === entry.id ? (
                      <div className="my-library-confirm-delete">
                        <span>{t('bookClub.myLibrary.removeConfirm')}</span>
                        <button className="my-library-confirm-yes" onClick={() => handleRemove(entry.id)}>{t('bookClub.common.yes')}</button>
                        <button className="my-library-confirm-no" onClick={() => setConfirmDeleteId(null)}>{t('bookClub.common.no')}</button>
                      </div>
                    ) : (
                      <button
                        className="my-library-remove-btn"
                        onClick={() => setConfirmDeleteId(entry.id)}
                      >
                        🗑️ {t('bookClub.common.remove')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MyLibrary;
